/**
 * Helpers for letting a user save/print the click-through agreement for their
 * records. We deliberately avoid a PDF dependency: we open the agreement in a
 * clean, print-styled document and hand off to the browser's print dialog,
 * which offers "Save as PDF" on every major platform.
 */

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Build a self-contained, print-styled HTML document for an agreement.
 *
 * `contentHtml` is the server-authored, trusted agreement body (same source as
 * the in-app `v-html`); only the title/version we interpolate are escaped. The
 * print CSS mirrors the in-app prose styling so ordered/nested lists keep their
 * indentation on paper. The document `<title>` becomes the default PDF filename.
 */
export function buildAgreementPrintHtml(
  title: string,
  contentHtml: string,
  version?: string,
): string {
  const safeTitle = escapeHtml(title)
  const footer = version
    ? `<footer>Version ${escapeHtml(version)}</footer>`
    : ''
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${safeTitle}</title>
<style>
  :root { color-scheme: light; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; color: #1a1a1a; line-height: 1.6; max-width: 780px; margin: 2rem auto; padding: 0 1.5rem; }
  p { margin: 0 0 0.75rem; }
  ol, ul { padding-left: 1.5rem; margin: 0 0 0.75rem; }
  li { margin-bottom: 0.375rem; }
  li > ol, li > ul { margin-top: 0.375rem; }
  blockquote { margin: 0 0 0.75rem; padding-left: 1rem; border-left: 3px solid #ddd; color: #555; }
  footer { margin-top: 2rem; padding-top: 1rem; border-top: 1px solid #ddd; font-size: 0.8rem; color: #666; }
  @media print { body { margin: 0; max-width: none; } }
</style>
</head>
<body>
${contentHtml}
${footer}
</body>
</html>`
}

/**
 * Open the given HTML in a new window and trigger the browser's print dialog.
 * Returns false if the popup was blocked (nothing was opened).
 */
export function openPrintWindow(html: string): boolean {
  // Do NOT pass the `noopener` window feature: `window.open(url, target,
  // 'noopener')` returns `null`, leaving us unable to write the document or
  // call print() — the popup would sit blank on about:blank. Open normally to
  // get a handle, then sever the child's `opener` back-reference ourselves
  // (reverse-tabnabbing guard). The HTML we write is our own, server-sanitized
  // agreement content.
  const win = window.open('', '_blank')
  if (!win) return false
  win.opener = null
  win.document.write(html)
  win.document.close()
  win.focus()
  win.print()
  return true
}

/** Convenience: build + open the print view for an agreement. */
export function printAgreement(
  title: string,
  contentHtml: string,
  version?: string,
): boolean {
  return openPrintWindow(buildAgreementPrintHtml(title, contentHtml, version))
}
